import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Shield,
  Eye,
  Share2,
  Lock,
  Cookie,
  UserCheck,
  Clock,
  Baby,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  Database,
  Settings,
  Users,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    {
      id: 1,
      title: 'Information We Collect',
      icon: <Database className="section-icon" />,
      content:
        'To provide and improve Scrap Sector services, we collect information necessary to create your account, schedule pickups, process transactions, and provide a secure experience.',
      subsections: [
        {
          title: 'Personal Information',
          list: [
            'Full name',
            'Email address',
            'Phone number',
            'Pickup and delivery addresses',
            'Profile information',
            'Vehicle details (for Riders/Feriwalas where applicable)'
          ]
        },
        {
          title: 'Transaction Information',
          list: [
            'Scrap category and details',
            'Pickup requests and booking history',
            'Payment and transaction details',
            'Invoices and receipts'
          ]
        },
        {
          title: 'Content & Location Data',
          list: [
            'Photos of scrap uploaded through the app',
            'Messages with riders and customer support',
            'Location data (only with your permission)',
            'Location used to assign nearby riders and improve pickup accuracy'
          ]
        },
        {
          title: 'Device & Usage Information',
          list: [
            'Device model and operating system',
            'IP address',
            'Browser information',
            'Cookies and similar technologies',
            'Crash reports',
            'Application logs and analytics'
          ]
        }
      ],
      color: '#2F9621'
    },

    {
      id: 2,
      title: 'How We Use Your Information',
      icon: <Settings className="section-icon" />,
      content:
        'We use your information only for legitimate business purposes and to provide a better Scrap Sector experience.',
      list: [
        'Create and manage your account',
        'Verify your identity using OTP authentication',
        'Schedule and manage scrap pickups',
        'Connect customers with nearby riders',
        'Process transactions and payments',
        'Provide customer support',
        'Improve our products and services',
        'Detect fraud and suspicious activities',
        'Maintain platform security',
        'Send booking updates and service notifications',
        'Send marketing communications only with your consent',
        'Comply with legal obligations'
      ],
      color: '#2F9621'
    },

    {
      id: 3,
      title: 'Information Sharing & Disclosure',
      icon: <Share2 className="section-icon" />,
      content:
        'Scrap Sector respects your privacy and does not sell your personal information. We only share information when necessary to provide our services, operate the platform, comply with legal obligations, or protect the rights and safety of our users.',
      subsections: [
        {
          title: 'Customer & Pickup Partner Information',
          list: [
            'Customer name, phone number, pickup address, and scrap details may be shared with the assigned pickup partner to complete the scheduled pickup.',
            'Pickup partners (Riders/Feriwalas) may share their name, profile information, contact details, and live location with customers only during an active pickup.',
            'Information is shared only to facilitate successful order completion and improve service quality.'
          ]
        },
        {
          title: 'Service Providers',
          list: [
            'Cloud hosting providers',
            'Payment gateway providers',
            'SMS & OTP service providers',
            'Analytics and crash reporting services',
            'Customer support platforms',
            'Mapping and navigation providers such as Google Maps'
          ]
        },
        {
          title: 'Business Partners',
          list: [
            'Authorized recycling partners where required for processing scrap.',
            'Insurance providers or verification partners when necessary to provide our services.'
          ]
        },
        {
          title: 'Legal Requirements',
          list: [
            'Comply with applicable laws and regulations.',
            'Respond to lawful requests from government authorities, courts, or law enforcement agencies.',
            'Prevent fraud, illegal activities, or misuse of the platform.',
            'Protect the rights, safety, and property of Scrap Sector, our customers, pickup partners, and the public.',
            'Enforce our Terms of Service and other legal agreements.'
          ]
        }
      ],
      color: '#2F9621'
    },

    {
      id: 4,
      title: 'Data Security',
      icon: <Lock className="section-icon" />,
      content:
        'We implement industry-standard technical and organizational security measures to safeguard your personal information against unauthorized access, misuse, alteration, or disclosure.',
      list: [
        'Encrypted transmission of sensitive information',
        'Secure cloud infrastructure',
        'Access controls and authentication',
        'Regular security monitoring',
        'Secure payment processing',
        'Periodic security reviews',
        'Continuous platform monitoring'
      ],
      color: '#2F9621'
    },

    {
      id: 5,
      title: 'Cookies & Similar Technologies',
      icon: <Cookie className="section-icon" />,
      content:
        'Our website and services use cookies and similar technologies to improve functionality, security, and user experience.',
      subsections: [
        {
          title: 'Cookies are used for:',
          list: [
            'Remembering login sessions',
            'Saving user preferences',
            'Website performance improvements',
            'Traffic analytics',
            'Fraud prevention',
            'Security monitoring'
          ]
        }
      ],
      color: '#2F9621'
    },

    {
      id: 6,
      title: 'Your Rights & Choices',
      icon: <UserCheck className="section-icon" />,
      content:
        'You have control over your personal information and may exercise your privacy rights in accordance with applicable laws.',
      list: [
        'Access your personal information',
        'Update or correct inaccurate information',
        'Delete your Scrap Sector account',
        'Request deletion of your personal data',
        'Withdraw consent for marketing communications',
        'Request information about how your data is processed',
        'Contact our support team regarding privacy concerns'
      ],
      color: '#2F9621'
    },

    {
      id: 7,
      title: 'Data Retention',
      icon: <Clock className="section-icon" />,
      content:
        'We retain your information only for as long as necessary to provide our services, comply with legal obligations, prevent fraud, resolve disputes, and enforce our agreements. If you request account deletion, Scrap Sector will delete or anonymize your personal information from active systems within a reasonable period. Certain records may be retained where required by law or for fraud prevention, taxation, dispute resolution, or other legitimate legal purposes.',
      color: '#2F9621'
    },

    {
      id: 8,
      title: "Children's Privacy",
      icon: <Baby className="section-icon" />,
      content:
        'Scrap Sector is intended only for individuals who are 18 years of age or older. We do not knowingly collect personal information from anyone under the age of 18. If you believe that a minor has provided us with personal information, please contact us immediately so that appropriate action can be taken.',
      color: '#2F9621'
    },

    {
      id: 9,
      title: 'Third-Party Services',
      icon: <Users className="section-icon" />,
      content:
        'Our platform may integrate with trusted third-party services to provide and improve our services. These providers operate under their own privacy policies.',
      list: [
        'Google Maps services',
        'Payment gateway providers',
        'Cloud hosting providers',
        'SMS & OTP service providers',
        'Analytics providers',
        'Push notification services',
        'Customer support platforms'
      ],
      color: '#2F9621'
    },

    {
      id: 10,
      title: 'Permissions Used',
      icon: <Eye className="section-icon" />,
      content:
        'Scrap Sector requests only the permissions necessary to provide its core services.',
      list: [
        'Location – to find nearby riders and schedule pickups',
        'Camera – to capture photos of scrap items',
        'Photos/Storage – to upload scrap images',
        'Phone – for OTP verification (where applicable)',
        'Notifications – to send booking, pickup, and service updates'
      ],
      color: '#2F9621'
    },

    {
      id: 11,
      title: 'Changes to This Privacy Policy',
      icon: <RefreshCw className="section-icon" />,
      content:
        "We may update this Privacy Policy from time to time to reflect changes in our services, technology, or legal requirements. Any updates will be posted on this page with a revised 'Last Updated' date. Your continued use of Scrap Sector after such changes constitutes your acceptance of the updated Privacy Policy.",
      color: '#2F9621'
    }
  ];

  return (
    <div className="privacy-page">

      {/* =====================================================
          BACKGROUND ELEMENTS
      ====================================================== */}

      <div className="background-elements">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
      </div>

      <div className="privacy-container">

        {/* =====================================================
            HERO
        ====================================================== */}

        <motion.div
          className="privacy-hero"
          initial={{
            opacity: 0,
            y: -50
          }}
          animate={{
            opacity: isVisible ? 1 : 0,
            y: isVisible ? 0 : -50
          }}
          transition={{
            duration: 0.8
          }}
        >

          <div className="hero-content">

            <motion.div
              className="hero-icon"
              initial={{
                opacity: 0,
                scale: 0.7
              }}
              animate={{
                opacity: 1,
                scale: 1
              }}
              transition={{
                duration: 0.7,
                delay: 0.2
              }}
            >
              <Shield className="main-icon" />

              <div className="icon-glow"></div>
            </motion.div>

            <div className="hero-label">
              <Shield size={15} />
              <span>SCRAPSECTOR • PRIVACY</span>
            </div>

            <h1 className="hero-title">
              Privacy
              <span> Policy</span>
            </h1>

            <p className="hero-subtitle">
              Learn how Scrap Sector collects, uses, stores, and
              protects your personal information when you use our services.
            </p>

            <div className="hero-badge">
              <Lock className="badge-icon" />

              <span>
                Last Updated: July 15, 2026
              </span>
            </div>

          </div>
        </motion.div>

        {/* =====================================================
            INTRO / TRUST STRIP
        ====================================================== */}

        <motion.div
          className="privacy-trust-strip"
          initial={{
            opacity: 0,
            y: 25
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          transition={{
            duration: 0.7,
            delay: 0.3
          }}
        >

          <div className="trust-item">
            <div className="trust-icon">
              <Lock />
            </div>

            <div>
              <strong>Secure</strong>
              <span>Your information is protected</span>
            </div>
          </div>

          <div className="trust-divider"></div>

          <div className="trust-item">
            <div className="trust-icon">
              <Eye />
            </div>

            <div>
              <strong>Transparent</strong>
              <span>Clear information practices</span>
            </div>
          </div>

          <div className="trust-divider"></div>

          <div className="trust-item">
            <div className="trust-icon">
              <UserCheck />
            </div>

            <div>
              <strong>Your Control</strong>
              <span>Manage your privacy choices</span>
            </div>
          </div>

        </motion.div>

        {/* =====================================================
            CONTENT GRID
        ====================================================== */}

        <div className="privacy-grid">

          {sections.map((section, index) => (

            <motion.div
              key={section.id}
              className="privacy-card"
              initial={{
                opacity: 0,
                y: 50,
                scale: 0.96
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }}
              transition={{
                duration: 0.6,
                delay: index * 0.08,
                type: 'spring',
                stiffness: 100
              }}
              whileHover={{
                y: -8,
                transition: {
                  duration: 0.2
                }
              }}
            >

              {/* Card Top */}

              <div
                className="card-header"
                style={{
                  background:
                    'linear-gradient(135deg, #102018 0%, #1F6418 100%)'
                }}
              >

                <div className="card-number">
                  {String(section.id).padStart(2, '0')}
                </div>

                <div
                  className="card-icon"
                  style={{
                    color: '#2F9621'
                  }}
                >
                  {section.icon}
                </div>

                <div className="card-header-line"></div>

              </div>

              {/* Card Body */}

              <div className="card-body">

                <div className="card-heading-row">

                  <h3 className="card-title">
                    {section.title}
                  </h3>

                  <ChevronRight className="card-arrow" />

                </div>

                <p className="card-text">
                  {section.content}
                </p>

                {/* Main List */}

                {section.list && (
                  <ul className="card-list">

                    {section.list.map((item, idx) => (

                      <li
                        key={idx}
                        className="list-item"
                      >

                        <div
                          className="list-icon"
                          style={{
                            backgroundColor: '#EAF6E7'
                          }}
                        >
                          <CheckCircle
                            className="check-icon"
                            style={{
                              color: '#2F9621'
                            }}
                          />
                        </div>

                        <span>
                          {item}
                        </span>

                      </li>

                    ))}

                  </ul>
                )}

                {/* Subsections */}

                {section.subsections &&
                  section.subsections.map(
                    (subsection, subIdx) => (

                      <div
                        key={subIdx}
                        className="card-subsection"
                        style={{
                          borderColor: '#2F9621'
                        }}
                      >

                        <h4 className="subsection-title">
                          {subsection.title}
                        </h4>

                        <ul className="card-list">

                          {subsection.list.map(
                            (item, idx) => (

                              <li
                                key={idx}
                                className="list-item"
                              >

                                <div
                                  className="list-icon"
                                  style={{
                                    backgroundColor:
                                      '#EAF6E7'
                                  }}
                                >

                                  <CheckCircle
                                    className="check-icon"
                                    style={{
                                      color: '#2F9621'
                                    }}
                                  />

                                </div>

                                <span>
                                  {item}
                                </span>

                              </li>

                            )
                          )}

                        </ul>

                      </div>

                    )
                  )}

              </div>

            </motion.div>

          ))}

        </div>

        {/* =====================================================
            CONTACT SECTION
        ====================================================== */}

        <motion.section
          className="contact-section"
          initial={{
            opacity: 0,
            y: 50
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          transition={{
            duration: 0.8,
            delay: 0.5
          }}
        >

          <div className="contact-header">

            <div className="contact-label">
              <Shield size={16} />
              <span>PRIVACY SUPPORT</span>
            </div>

            <h3>
              Have a Privacy Question?
            </h3>

            <p>
              Have concerns about your data?
              Our team is here to help.
            </p>

          </div>

          <div className="contact-cards">

            {/* Email */}

            <motion.div
              className="contact-card"
              whileHover={{
                scale: 1.04,
                y: -4
              }}
              transition={{
                duration: 0.2
              }}
            >

              <div className="contact-icon-wrapper">
                <Mail className="contact-icon" />
              </div>

              <div className="contact-info">

                <h4>
                  Email Us
                </h4>

                <span>
                  scrapsectorbusiness@gmail.com
                </span>

              </div>

              <ExternalLink className="contact-arrow" />

            </motion.div>

            {/* Phone */}

            <motion.div
              className="contact-card"
              whileHover={{
                scale: 1.04,
                y: -4
              }}
              transition={{
                duration: 0.2
              }}
            >

              <div className="contact-icon-wrapper">
                <Phone className="contact-icon" />
              </div>

              <div className="contact-info">

                <h4>
                  Call Us
                </h4>

                <span>
                  7278000038
                </span>

              </div>

              <ExternalLink className="contact-arrow" />

            </motion.div>

            {/* Address */}

            <motion.div
              className="contact-card contact-card-location"
              whileHover={{
                scale: 1.04,
                y: -4
              }}
              transition={{
                duration: 0.2
              }}
            >

              <div className="contact-icon-wrapper">
                <MapPin className="contact-icon" />
              </div>

              <div className="contact-info">

                <h4>
                  Visit Us
                </h4>

                <span>
                  Manakpur, Thakurdass, Near Railway Crossing
                  (Peer Baba), Kalka, Pinjore, Panchkula,
                  Haryana, 134102
                </span>

              </div>

              <ExternalLink className="contact-arrow" />

            </motion.div>

          </div>

        </motion.section>

        {/* =====================================================
            FOOTER ACTION
        ====================================================== */}

        <motion.div
          className="privacy-footer"
          initial={{
            opacity: 0,
            y: 30
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          transition={{
            duration: 0.6,
            delay: 1
          }}
        >

          <Link
            to="/"
            className="back-home-btn"
          >

            <ArrowLeft className="btn-icon" />

            <span>
              Back to Home
            </span>

            <div className="btn-glow"></div>

          </Link>

        </motion.div>

      </div>
    </div>
  );
};

export default PrivacyPolicy;